import { flip, offset, shift } from '@floating-ui/dom';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { TopicRailHover } from './useTopicRailHover';

interface TopicRailTooltipProps {
  hover: TopicRailHover | null;
  topicColorMap: ReadonlyMap<string, string>;
}

/**
 * Names the topic behind a rail in the transcript's left gutter, on hover.
 *
 * Anchorless / controlled: the rail is a `::before` pseudo-element with no
 * element of its own to hang a `data-tooltip-id` on, so `useTopicRailHover`
 * hit-tests the gutter and this positions the tooltip at those viewport
 * coordinates (fixed strategy — the pane clips its overflow).
 */
export const TopicRailTooltip: React.FC<TopicRailTooltipProps> = ({
  hover,
  topicColorMap,
}) => (
  <Tooltip
    id="topic-rail-tooltip"
    imperativeModeOnly
    isOpen={!!hover}
    position={hover ? { x: hover.x, y: hover.y } : undefined}
    // Above the rail's top, left-aligned to it. Opening leftward is what the
    // gutter's position rules out — there is never a tooltip's width between
    // it and the window edge, so floating-ui flips it back over the sentence
    // the rail marks. Upward always fits, and the only text it can overlap is
    // the line ABOVE the match rather than the match itself; the small spill
    // to the right of the rail is the trade for that.
    place="top-start"
    // Fresh array per render on purpose — react-tooltip pushes its own arrow
    // middleware into whatever array it is handed.
    middlewares={[
      offset(6),
      // Only the first line of the pane has nothing above it.
      flip({ fallbackPlacements: ['bottom-start'] }),
      shift({ padding: 8 }),
    ]}
    positionStrategy="fixed"
    // Height-locked single-line look, relaxed for the multi-topic case: a
    // paragraph can match several selected topics and each gets a row.
    style={{ ...COMPACT_TOOLTIP_STYLE, height: 'auto', padding: '6px 10px' }}
  >
    {hover && (
      <TopicRows>
        {hover.titles.map(title => (
          <TopicRow key={title}>
            <TopicDot $color={topicColorMap.get(title)} />
            {title}
          </TopicRow>
        ))}
      </TopicRows>
    )}
  </Tooltip>
);

const TopicRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
`;

const TopicRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TopicDot = styled.span<{ $color?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => props.$color ?? 'var(--badge-blue-text)'};
`;
